import React, { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { Table, Col, Row, Modal, ModalHeader, ModalBody, Spinner, UncontrolledAlert } from "reactstrap"
import api from "API/Api"
import { Link } from "react-router-dom"
import SweetAlert from "react-bootstrap-sweetalert"
import loader from "./../../assets/loading/loader.gif"
import { usePermissions } from "API/PermissionContext"

const Companies = () => {
  const { canAdd, canEdit, canDelete, canExport, MODULE } = usePermissions()
  const MID = MODULE.COMPANY
  const [companies, setCompanies] = useState([])
  const [dataLoaded, setDataLoaded] = useState(false)
  const [checkedItems, setCheckedItems] = useState({})
  const [parentChecked, setParentChecked] = useState(false)
  const [confirm_alert, setconfirm_alert] = useState(false)
  const [confirm_multiple_alert, setconfirm_multiple_alert] = useState(false)
  const [success_dlg, setsuccess_dlg] = useState(false)
  const [dynamic_title, setdynamic_title] = useState("")
  const [dynamic_description, setdynamic_description] = useState("")
  const [companyId, setCompanyId] = useState("")
  const [search, setSearch] = useState({ name: "", number: "", village: "", district: "", state: "" })
  const [showFilter, setShowFilter] = useState(false)
  // Import modal
  const [importModal, setImportModal] = useState(false)
  const [importFile, setImportFile] = useState(null)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState(null)
  const fileRef = useRef(null)

  const fetchData = useCallback(async () => {
    try {
      const resp = await api.get("companies")
      const d = Array.isArray(resp.data.data) ? resp.data.data : resp.data.data?.data || []
      setCompanies(d)
    } catch (err) { console.error(err) }
    setDataLoaded(true)
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const handleDelete = async () => {
    try { await api.delete("delete-company/" + companyId); fetchData() } catch (e) { console.error(e) }
  }
  const handleMultiDelete = async () => {
    const ids = Object.keys(checkedItems).filter(k => checkedItems[k])
    if (!ids.length) return
    try { await api.post("delete-multiple-companies", { items: ids }); setCheckedItems({}); setParentChecked(false); fetchData() } catch (e) { console.error(e) }
  }
  const handleCheckbox = id => {
    setCheckedItems(prev => {
      const n = { ...prev, [id]: !prev[id] }
      setParentChecked(companies.length > 0 && companies.every(c => n[c.id]))
      return n
    })
  }
  const handleParentCheckbox = () => {
    const n = !parentChecked; setParentChecked(n)
    const items = {}; companies.forEach(c => { items[c.id] = n }); setCheckedItems(items)
  }
  const selectedCount = Object.values(checkedItems).filter(Boolean).length

  // Filters
  const uniqueStates = useMemo(() => [...new Set(companies.map(c => c.stateName).filter(Boolean))].sort(), [companies])
  const uniqueDistricts = useMemo(() => [...new Set(companies.map(c => c.districtName).filter(Boolean))].sort(), [companies])

  const filtered = useMemo(() => {
    return companies.filter(c => {
      return (c.name || "").toLowerCase().includes(search.name.toLowerCase()) &&
        (c.number || "").toLowerCase().includes(search.number.toLowerCase()) &&
        (c.village || "").toLowerCase().includes(search.village.toLowerCase()) &&
        (c.districtName || "").toLowerCase().includes(search.district.toLowerCase()) &&
        (c.stateName || "").toLowerCase().includes(search.state.toLowerCase())
    })
  }, [companies, search])

  // CSV Export
  const exportCSV = () => {
    if (!filtered.length) return
    const headers = ["S.No", "Name", "Number", "Door", "Village", "Taluk", "District", "State", "Pincode", "GSTIN", "Billing Tax Type"]
    const rows = filtered.map((c, i) => [
      i + 1, c.name, c.number, c.door, c.village, c.taluk,
      c.districtName || "", c.stateName || "", c.pincode, c.gstIn, c.billingTaxType
    ])
    const csvContent = [headers, ...rows].map(r => r.map(v => `"${(v || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n")
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a"); a.href = url; a.download = `companies_${new Date().toISOString().slice(0, 10)}.csv`
    a.click(); URL.revokeObjectURL(url)
  }

  // Bulk Import
  const handleImport = async () => {
    if (!importFile) return
    setImporting(true); setImportResult(null)
    try {
      const formData = new FormData()
      formData.append("file", importFile)
      const resp = await api.post("import-companies", formData, { headers: { "Content-Type": "multipart/form-data" } })
      setImportResult(resp.data)
      if (resp.data.success) fetchData()
    } catch (e) {
      setImportResult({ success: false, message: e.response?.data?.message || "Import failed" })
    }
    setImporting(false)
  }

  const downloadSample = async () => {
    try {
      const resp = await api.get("sample-companies-export", { responseType: "blob" })
      const url = URL.createObjectURL(new Blob([resp.data]))
      const a = document.createElement("a"); a.href = url; a.download = "companies_sample_import.csv"
      a.click(); URL.revokeObjectURL(url)
    } catch (e) { console.error(e) }
  }

  const removeFilter = () => {
    setSearch({ name: "", number: "", village: "", district: "", state: "" })
    setShowFilter(!showFilter)
  }

  // Summary stats
  const totalCompanies = companies.length
  const stateCount = new Set(companies.map(c => c.stateName).filter(Boolean)).size
  const districtCount = new Set(companies.map(c => c.districtName).filter(Boolean)).size
  const gstCompanies = companies.filter(c => c.billingTaxType === "gst").length

  const summaryCards = [
    { label: "Total Companies", value: totalCompanies, icon: "fa-building", color: "#6366f1" },
    { label: "States", value: stateCount, icon: "fa-map-marker-alt", color: "#0ea5e9" },
    { label: "Districts", value: districtCount, icon: "fa-map", color: "#22c55e" },
    { label: "GST Registered", value: gstCompanies, icon: "fa-file-invoice", color: "#f59e0b" },
  ]

  return (
    <>
      <div className="dashBody">


        <div className="font-size-18 mb-0 dashHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <h4 style={{ margin: 0 }}>Companies</h4>
            {canAdd(MID) && (
              <Link to="/add-company" title="Add Company"
                style={{ width: "32px", height: "32px", borderRadius: "4px", background: "#28a745", color: "#fff",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", textDecoration: "none" }}>
                <i className="fas fa-plus"></i>
              </Link>
            )}
            {canDelete(MID) && (
              <button title={selectedCount > 0 ? `Delete ${selectedCount} selected` : "Select items to delete"}
                onClick={() => { if (selectedCount > 0) setconfirm_multiple_alert(true); else alert("Please select at least one company to delete.") }}
                style={{ width: "32px", height: "32px", borderRadius: "4px", background: "#dc3545", color: "#fff",
                  border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", cursor: "pointer", opacity: selectedCount > 0 ? 1 : 0.7 }}>
                <i className="fas fa-trash"></i>
              </button>
            )}
            <button title="Toggle Filter" onClick={removeFilter}
              style={{ width: "32px", height: "32px", borderRadius: "4px", background: "#0d6efd", color: "#fff",
                border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", cursor: "pointer" }}>
              <i className="fas fa-filter"></i>
            </button>
            {canExport(MID) && (
              <button title="Export CSV" onClick={exportCSV} disabled={!filtered.length}
                style={{ width: "32px", height: "32px", borderRadius: "4px", background: "#17a2b8", color: "#fff",
                  border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", cursor: "pointer", opacity: filtered.length ? 1 : 0.5 }}>
                <i className="fas fa-download"></i>
              </button>
            )}
            {canAdd(MID) && (
              <button title="Import CSV" onClick={() => setImportModal(true)}
                style={{ width: "32px", height: "32px", borderRadius: "4px", background: "#6f42c1", color: "#fff",
                  border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", cursor: "pointer" }}>
                <i className="fas fa-file-upload"></i>
              </button>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <input type="text" className="form-control form-control-sm" placeholder="Search"
              style={{ width: "180px", borderRadius: "4px 0 0 4px" }}
              value={search.name} onChange={e => setSearch(s => ({ ...s, name: e.target.value }))} />
            <button className="btn btn-primary btn-sm" style={{ borderRadius: "0 4px 4px 0", height: "31px" }}>
              <i className="fas fa-search"></i>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <Row className="mb-3 mt-2">
          {summaryCards.map((card, i) => (
            <Col md={3} sm={6} key={i}>
              <div style={{
                background: "#fff", borderRadius: "10px", padding: "14px 16px",
                borderLeft: `4px solid ${card.color}`, boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px"
              }}>
                <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: card.color + "15",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className={`fas ${card.icon}`} style={{ color: card.color, fontSize: "16px" }}></i>
                </div>
                <div>
                  <div style={{ fontSize: "20px", fontWeight: "700", color: "#1e293b" }}>{card.value}</div>
                  <div style={{ fontSize: "11px", color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.5px" }}>{card.label}</div>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        <div className="table-responsive">
          <Table className="table mb-0 dairyTbl" id="companiesTbl">
            <thead>
              <tr>
                <th><input type="checkbox" checked={parentChecked} onChange={handleParentCheckbox} /></th>
                <th>S.NO</th>
                <th>Name</th>
                <th>Number</th>
                <th>Village</th>
                <th>District</th>
                <th>State</th>
                <th>Pincode</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {showFilter && (
                <tr>
                  <td></td><td></td>
                  <td><input type="text" className="form-control form-control-sm" placeholder="Name..."
                    value={search.name} onChange={e => setSearch(s => ({ ...s, name: e.target.value }))} /></td>
                  <td><input type="text" className="form-control form-control-sm" placeholder="Number..."
                    value={search.number} onChange={e => setSearch(s => ({ ...s, number: e.target.value }))} /></td>
                  <td><input type="text" className="form-control form-control-sm" placeholder="Village..."
                    value={search.village} onChange={e => setSearch(s => ({ ...s, village: e.target.value }))} /></td>
                  <td>
                    <select className="form-control form-control-sm" value={search.district}
                      onChange={e => setSearch(s => ({ ...s, district: e.target.value }))}>
                      <option value="">All</option>
                      {uniqueDistricts.map((d, i) => <option key={i} value={d}>{d}</option>)}
                    </select>
                  </td>
                  <td>
                    <select className="form-control form-control-sm" value={search.state}
                      onChange={e => setSearch(s => ({ ...s, state: e.target.value }))}>
                      <option value="">All</option>
                      {uniqueStates.map((st, i) => <option key={i} value={st}>{st}</option>)}
                    </select>
                  </td>
                  <td></td><td></td>
                </tr>
              )}
              {!dataLoaded ? (
                <tr><td colSpan={9}><img className="loaderImg" src={loader} alt="Loading" /></td></tr>
              ) : filtered.length > 0 ? (
                filtered.map((company, i) => (
                  <tr key={company.id}>
                    <td><input type="checkbox" checked={!!checkedItems[company.id]} onChange={() => handleCheckbox(company.id)} /></td>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: "600" }}>{company.name}</td>
                    <td>{company.number}</td>
                    <td>{company.village}</td>
                    <td>{company.districtName || "-"}</td>
                    <td>{company.stateName || "-"}</td>
                    <td>{company.pincode}</td>
                    <td>
                      <div style={{ display: "flex", gap: "4px" }}>
                        {canEdit(MID) && <Link title="Edit" to={"/edit-company/" + company.id}
                          style={{ width: "28px", height: "28px", borderRadius: "4px", background: "#0d6efd", color: "#fff",
                            display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "12px" }}>
                          <i className="fas fa-edit"></i></Link>}
                        {canEdit(MID) && <Link title="Settings" to={"/company-settings/" + company.id}
                          style={{ width: "28px", height: "28px", borderRadius: "4px", background: "#6f42c1", color: "#fff",
                            display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "12px" }}>
                          <i className="fas fa-cog"></i></Link>}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={9} className="text-center py-4">
                  <i className="fas fa-building fa-2x text-muted mb-2 d-block"></i>No companies found
                </td></tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Import Modal */}
      <Modal isOpen={importModal} toggle={() => { setImportModal(false); setImportResult(null); setImportFile(null) }} size="md">
        <ModalHeader toggle={() => setImportModal(false)}>
          <i className="fas fa-file-upload me-2"></i> Bulk Import Companies
        </ModalHeader>
        <ModalBody>
          <div style={{ marginBottom: "16px" }}>
            <button className="btn btn-outline-primary btn-sm mb-3" onClick={downloadSample}>
              <i className="fas fa-download me-1"></i> Download Sample CSV
            </button>
            <p style={{ fontSize: "13px", color: "#6c757d" }}>
              Download the sample CSV, fill in your company data, and upload it below. State and District names will be automatically resolved.
            </p>
          </div>
          <div className="mb-3">
            <label className="form-label"><strong>Select CSV File</strong></label>
            <input type="file" ref={fileRef} className="form-control" accept=".csv,.xlsx,.xls,.txt"
              onChange={e => { setImportFile(e.target.files[0]); setImportResult(null) }} />
          </div>
          {importResult && (
            <div className={`alert alert-${importResult.success ? "success" : "danger"}`} style={{ fontSize: "13px" }}>
              <strong>{importResult.message}</strong>
              {importResult.errors && importResult.errors.length > 0 && (
                <ul className="mt-2 mb-0">{importResult.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}</ul>
              )}
            </div>
          )}
          <div className="text-end">
            <button className="btn btn-primary" onClick={handleImport} disabled={!importFile || importing}>
              {importing ? <><Spinner size="sm" /> Importing...</> : <><i className="fas fa-upload me-1"></i> Import</>}
            </button>
          </div>
        </ModalBody>
      </Modal>

      {confirm_alert && (
        <SweetAlert title="Are you sure?" warning showCancel confirmBtnBsStyle="success" cancelBtnBsStyle="danger"
          onConfirm={() => { handleDelete(); setconfirm_alert(false); setsuccess_dlg(true); setdynamic_title("Deleted"); setdynamic_description("Company deleted.") }}
          onCancel={() => setconfirm_alert(false)}>You won't be able to revert this!</SweetAlert>
      )}
      {confirm_multiple_alert && (
        <SweetAlert title={`Delete ${selectedCount} companies?`} warning showCancel confirmBtnBsStyle="success" cancelBtnBsStyle="danger"
          onConfirm={() => { handleMultiDelete(); setconfirm_multiple_alert(false); setsuccess_dlg(true); setdynamic_title("Deleted"); setdynamic_description("Companies deleted.") }}
          onCancel={() => setconfirm_multiple_alert(false)}>You won't be able to revert this!</SweetAlert>
      )}
      {success_dlg && (
        <SweetAlert success title={dynamic_title} onConfirm={() => setsuccess_dlg(false)}>{dynamic_description}</SweetAlert>
      )}
    </>
  )
}

export default Companies
